/**
 * STUDENT SEARCH COMPONENT
 *
 * Advanced search interface for finding students in the Vermont education system.
 * This component provides:
 *
 * - Complex query building with multiple search criteria
 * - Integration with student information systems
 * - Authentication-protected access for authorized personnel
 * - Persistent search results using localStorage
 * - Real-time search with loading states
 *
 * Primary users: School administrators, registrars, guidance counselors
 * Purpose: Locate students for enrollment, transfer, or demographic analysis
 *
 * Security: Requires Azure AD authentication to access student data
 */

import React, { useState, useEffect } from 'react'
import { Collapse } from 'react-bootstrap'
import { useIsAuthenticated } from '@azure/msal-react'
// Child components for search functionality
import QueryBuilder from './components/QueryBuilder'
import SearchResults from './components/SearchResults'
import StudentSearchInstructions from './components/StudentSearchInstructions'
import NotAuthorizedModal from '../../modals/NotAuthorizedModal'

export default function StudentSearch() {
  const isAuthenticated = useIsAuthenticated()

  /**
   * SEARCH STATE MANAGEMENT
   *
   * Manages search results, loading states, and modal visibility.
   * Search results are persisted to localStorage to maintain state
   * across navigation and page refreshes.
   */
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [instructionsOpen, setInstructionsOpen] = useState(false)

  /**
   * SEARCH RESULTS PERSISTENCE
   *
   * Restore previous search results from localStorage on component mount.
   * This maintains user context when navigating between different sections
   * of the application.
   */
  useEffect(() => {
    const storedResults = window.localStorage.getItem('searchResults')
    if (storedResults) {
      const parsed = JSON.parse(storedResults)
      setSearchResults(parsed)
      if (parsed.length > 0) setHasSearched(true)
    }
  }, [])

  const handleDelete = permNumber => {
    setSearchResults(prev => {
      const updated = prev.filter(s => s.permNumber !== permNumber)
      window.localStorage.setItem('searchResults', JSON.stringify(updated))
      return updated
    })
  }

  /**
   * AUTHENTICATION GUARD
   *
   * Prevents unauthorized access to student data by showing a modal
   * for unauthenticated users. This is a critical security feature
   * protecting student privacy and FERPA compliance.
   */
  if (!isAuthenticated) {
    return <NotAuthorizedModal show={true} onHide={() => {}} />
  }

  /**
   * SEARCH INTERFACE LAYOUT
   *
   * Provides a comprehensive search interface with:
   * - Collapsible instructions for user guidance
   * - QueryBuilder for constructing complex searches
   * - SearchResults for displaying and managing results
   * - Conditional rendering based on search state
   */
  return (
    <main aria-labelledby="student-search-heading">
      <h5 id="student-search-heading" className="text-center mb-0 p-2">
        Student Search
      </h5>
      {/* Collapsible help section */}
      <button
        type="button"
        className="btn btn-link m-0 mb-2 align-self-end"
        onClick={() => setInstructionsOpen(o => !o)}
        aria-expanded={instructionsOpen}
        aria-controls="student-search-instructions"
        style={{ padding: 0 }}>
        What's this?
        <span className="visually-hidden"> — instructions for student search</span>
      </button>
      <hr />
      <Collapse in={instructionsOpen}>
        <div id="student-search-instructions">
          <StudentSearchInstructions />
        </div>
      </Collapse>
      {/* Main search interface */}
      <QueryBuilder setSearchResults={setSearchResults} setLoading={setLoading} setHasSearched={setHasSearched} />
      {/* Conditional results display - shows during loading or after a search */}
      {(loading || hasSearched) && <SearchResults searchResults={searchResults} loading={loading} onDelete={handleDelete} />}
    </main>
  )
}
